import { css } from "@emotion/css";
import { EmptyStateNoResults, ErrorPanel } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { InlineQueryResult } from "@octopusdeploy/octopus-react-client";
import type { ActivityElement, GetKubernetesManifestsDiffsResponse, GetKubernetesResourceManifestSummariesResponse, KubernetesTaskResourceStatusResource } from "@octopusdeploy/octopus-server-client";
import React, { useMemo, useState } from "react";
import type { StepWithKubernetesAction } from "~/areas/tasks/components/Task/K8sStatus/StepWithKubernetesAction";
import type { DiffSettings } from "~/areas/tasks/components/Task/Kubernetes/DiffConfigMenu";
import type { KubernetesDetailsFilter } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLayout";
import { ManifestNavigationTree } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestNavigationTree";
import { ManifestNavigationTreeLoadingSkeleton } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestNavigationTreeLoadingSkeleton";
import { ManifestViewerList } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestViewerList";
import { ManifestViewerListLoadingSkeleton } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestViewerListLoadingSkeleton";
import { StickyResizingContainer } from "~/areas/tasks/components/Task/Kubernetes/Manifests/StickyResizingContainer";
import { useKubernetesStepsWithStatus } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useKubernetesStepsWithStatus";
import { useManifestDiffsTreeAndOrderedListItems } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useManifestDiffsTreeAndOrderedListItems";
import { useManifestsTreeAndOrderedListItems } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useManifestsTreeAndOrderedListItems";
import type { AvailableTimelineItemResponse } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useTimelineItems";
import { DiffDeploymentSelector } from "~/areas/tasks/components/Task/Kubernetes/components/DiffDeploymentSelector";
import { createErrorInfo } from "../createErrorInfo";
const styles = {
    mainContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
    }),
    navigationContainer: css({
        padding: space[16],
        width: `calc(${space[16]} * 25)`, // 400px
        flexShrink: 0, //don't allow this to shrink
        "@media (width < 1024px)": {
            display: "none",
        },
    }),
    manifestsContainer: css({
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        paddingRight: space[16],
        paddingTop: space[16],
        "@media (width < 1024px)": {
            paddingLeft: space[16],
        },
    }),
    diffSelectorContainer: css({
        paddingTop: space[8],
        paddingBottom: space[16],
    }),
};
export type DiffQueryResult = InlineQueryResult<GetKubernetesManifestsDiffsResponse, null> & {
    selection: DeploymentDiffSelection;
};
export type DeploymentDiffSelection = {
    fromDeploymentId: string | undefined;
    toDeploymentId: string;
};
export type KubernetesAppliedManifestsProps = {
    steps: StepWithKubernetesAction[];
    activityLogs: Pick<ActivityElement, "Children">[];
    filter: KubernetesDetailsFilter;
    executionId: string;
    resourceStatusResult: KubernetesTaskResourceStatusResource | undefined;
    summariesResult: GetKubernetesResourceManifestSummariesResponse | undefined;
    diffsQuery: DiffQueryResult | undefined;
    showDiffs: boolean;
    diffSettings: DiffSettings;
    diffSelectionChanged: (updatedSelection: DeploymentDiffSelection) => void;
    priorTimelineItems?: AvailableTimelineItemResponse;
};
export const KubernetesAppliedManifests = (props: KubernetesAppliedManifestsProps) => {
    const [selectedManifestId, setSelectedManifestId] = useState<string | undefined>();
    const kubernetesStepsWithStatus = useKubernetesStepsWithStatus(props.steps, props.activityLogs);
    const [manifestTree, orderedManifestListItems, manifestCounts] = useManifestsTreeAndOrderedListItems(props.summariesResult?.Resources, props.filter, kubernetesStepsWithStatus);
    const { result: diffResult, isLoading: isLoadingDiffs, error: diffsError } = useManifestDiffsTreeAndOrderedListItems(props.diffsQuery, props.filter, props.diffSettings.hideUnchangedManifests, kubernetesStepsWithStatus);
    const onDiffDeploymentSelectionChanged = (fromDeploymentId: string) => {
        props.diffSelectionChanged({ fromDeploymentId, toDeploymentId: props.executionId });
    };
    const errorInfo = useMemo(() => createErrorInfo(diffsError), [diffsError]);
    const [manifestDiffTree, orderedManifestDiffListItems, manifestDiffCounts] = diffResult || [];
    const tree = props.showDiffs ? manifestDiffTree : manifestTree;
    const items = props.showDiffs ? orderedManifestDiffListItems : orderedManifestListItems;
    const counts = props.showDiffs ? manifestDiffCounts : manifestCounts;
    if (!props.showDiffs && (counts?.total === 0 || counts?.filteredTotal === 0)) {
        return <EmptyStateNoResults />;
    }
    return (<>
            <div className={styles.mainContainer}>
                <div className={styles.navigationContainer}>
                    {!errorInfo && isLoadingDiffs && <ManifestNavigationTreeLoadingSkeleton />}
                    {tree && (<StickyResizingContainer resizeToken={tree}>
                            <ManifestNavigationTree navigationTree={tree} onManifestSelected={setSelectedManifestId}/>
                        </StickyResizingContainer>)}
                </div>
                <div className={styles.manifestsContainer}>
                    {props.showDiffs && props.priorTimelineItems && (<div className={styles.diffSelectorContainer}>
                            <DiffDeploymentSelector comparableDeployments={props.priorTimelineItems.items} currentDeployment={props.priorTimelineItems.currentItem} onSelectionChanged={onDiffDeploymentSelectionChanged}/>
                        </div>)}
                    {errorInfo && <ErrorPanel error={errorInfo}/>}
                    {!errorInfo && isLoadingDiffs && <ManifestViewerListLoadingSkeleton />}
                    {items && counts && <ManifestViewerList manifestItems={items} itemCounts={counts} selectedItemId={selectedManifestId} diffViewType={props.diffSettings.viewType}/>}
                </div>
            </div>
            {props.showDiffs && (counts?.total === 0 || counts?.filteredTotal === 0) && <EmptyStateNoResults />}
        </>);
};
